/**
 * Avatar feature module — Character Creator catalog/recipe/name verbs plus
 * Tripo asset-generation status and cost. Case bodies moved verbatim from
 * embeddedBridge.ts.
 */

import type { HandlerMap } from '../dispatch';
import { useAvatarCreateStore } from '@/store/avatarCreateStore.ts';
import type { AvatarRecipe } from '@/components/AvatarCustomizer/avatarStore';

export interface SetCatalogMessage {
  type: 'setCatalog';
  payload: { catalog: import('@/components/AvatarCustomizer/avatarCatalog').AvatarCatalog };
}

export interface AvatarAssetReadyMessage {
  type: 'assetReady';
  payload: { category: import('@/components/AvatarCustomizer/avatarConfig').AssetCategory; assetId: string };
}

export interface AvatarAssetFailedMessage {
  type: 'assetFailed';
  payload: { category: import('@/components/AvatarCustomizer/avatarConfig').AssetCategory; assetId: string; reason: string };
}

export interface SetRecipeMessage {
  type: 'setRecipe';
  payload: { recipe: AvatarRecipe };
}

export interface SetCharacterNamesMessage {
  type: 'setCharacterNames';
  payload: { names: string[]; suggested: string };
}

export interface SetRequireNameMessage {
  type: 'setRequireName';
  payload: { requireName: boolean };
}

export interface SetLegacyAvailabilityMessage {
  type: 'setLegacyAvailability';
  payload: { show: boolean };
}

export interface SetTripoCharacterCostMessage {
  type: 'setTripoCharacterCost';
  payload: { cost: number };
}

/** 20260921 (owner) — the Characters tour's panels 4-5 play out inside `?page=avatar`, a SEPARATE
 *  WebView with its own JS context, so nothing here can read the board's tutorialStore. Unity pushes
 *  this on the customizer handshake (create mode, and only when the opening `openCharacterCustomizer`
 *  carried the tour hint); it is the page's ONLY signal that a tour is running. Spec §2.5. */
export interface SetCreatorTourMessage {
  type: 'setCreatorTour';
  payload: { kind: 'characters' };
}

export type AvatarMessage =
  | SetCatalogMessage
  | SetRecipeMessage
  | SetCharacterNamesMessage
  | SetRequireNameMessage
  | SetLegacyAvailabilityMessage
  | AvatarAssetReadyMessage
  | AvatarAssetFailedMessage
  | SetTripoCharacterCostMessage
  | SetCreatorTourMessage;

export const avatarHandlers = {
  setCatalog: (msg: SetCatalogMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      useAvatarStore.getState().setCatalog(msg.payload.catalog);
    });
  },
  setRecipe: (msg: SetRecipeMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      useAvatarStore.getState().setRecipe(msg.payload.recipe);
    });
  },
  setCharacterNames: (msg: SetCharacterNamesMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      useAvatarStore.getState().setCharacterNames(msg.payload.names ?? [], msg.payload.suggested ?? '');
    });
  },
  setRequireName: (msg: SetRequireNameMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      useAvatarStore.getState().setRequireName(msg.payload.requireName);
    });
  },
  setLegacyAvailability: (msg: SetLegacyAvailabilityMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      // Only an explicit `false` hides the escape: a launch context that never pushes this verb, or
      // pushes it with a missing payload, keeps the shipped visible button rather than silently
      // losing the legacy fallback.
      useAvatarStore.getState().setShowLegacyOption(msg.payload?.show !== false);
    });
  },
  assetReady: (msg: AvatarAssetReadyMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      useAvatarStore.getState().markAssetReady(msg.payload.category, msg.payload.assetId);
    });
  },
  assetFailed: (msg: AvatarAssetFailedMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      useAvatarStore.getState().markAssetFailed(msg.payload.category, msg.payload.assetId);
    });
  },
  setTripoCharacterCost: (msg: SetTripoCharacterCostMessage) => {
    useAvatarCreateStore.getState().setTripoCharacterCost(msg.payload.cost);
  },
  setCreatorTour: (msg: SetCreatorTourMessage) => {
    import('@/components/AvatarCustomizer/avatarStore').then(({ useAvatarStore }) => {
      // Only the ONE kind we ship arms the card; anything else (a missing payload from an older
      // native build, a future kind this bundle predates) clears the flag rather than arming a
      // tour whose panels this bundle cannot draw — the same "unrecognised input is ignored, never
      // defaulted" rule setLegacyAvailability above follows.
      useAvatarStore.getState().setCreatorTour(msg.payload?.kind === 'characters' ? 'characters' : null);
    });
  },
} satisfies HandlerMap;
